/**
 * Global UI state: theme, toasts, and the contextual paywall.
 */

import { create } from 'zustand';
import { PAYWALL_CONTEXT } from '@chordbookpro/shared';

const THEME_STORAGE_KEY = 'chordbookpro.theme';

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    return 'system';
  }
}

/**
 * Writes the theme to the document.
 * `system` removes the attribute so the prefers-color-scheme query applies.
 */
function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);

  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage can be unavailable; the theme still applies for this session.
  }
}

let toastId = 0;

export const useUiStore = create((set, get) => ({
  theme: readStoredTheme(),

  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },

  /* --------------------------------------------------------------- toasts */
  toasts: [],

  showToast: ({ message, tone = 'info', duration = 5000, action = null }) => {
    const id = ++toastId;
    set({ toasts: [...get().toasts, { id, message, tone, action }] });

    if (duration > 0) {
      setTimeout(() => get().dismissToast(id), duration);
    }
    return id;
  },

  dismissToast: (id) => set({ toasts: get().toasts.filter((toast) => toast.id !== id) }),

  /* -------------------------------------------------------------- paywall */
  paywall: { open: false, context: PAYWALL_CONTEXT.GENERAL_UPGRADE, details: null },

  /**
   * Opens the paywall.
   *
   * The paywall never traps the user — it is always dismissible, and the Free
   * plan remains fully usable behind it.
   */
  openPaywall: (context = PAYWALL_CONTEXT.GENERAL_UPGRADE, details = null) =>
    set({ paywall: { open: true, context, details } }),

  closePaywall: () => set({ paywall: { ...get().paywall, open: false } }),

  /* --------------------------------------------------------- mobile nav */
  mobileNavOpen: false,
  setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),
}));

// Apply the stored theme once at module load, matching the inline script in
// index.html that prevents a flash of the wrong colour scheme.
if (typeof document !== 'undefined') {
  applyTheme(readStoredTheme());
}
